// src/App.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams,
  Navigate,
} from "react-router-dom";

import Header from "./components/header";
import CategoryMenu from "./components/CategoryMenu";
import NewsList from "./components/NewsList";
import StoryPage from "./components/StoryPage";
import FooterBar from "./components/FooterBar";
import HashtagNewsList from "./components/HashtagNewsList";

import { fetchCategories } from "./api";

/**
 * Wrapper so NewsList is rendered via ONE route only.
 * This avoids unmount/mount blink on category changes.
 */
function NewsListRoute({ displayTitleMap }) {
  const { categoryId } = useParams();
  const category = categoryId || "breaking-news";

  return <NewsList category={category} displayTitleMap={displayTitleMap} />;
}

function AppShell() {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [catError, setCatError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  // ✅ derive current category ONLY from URL (single source of truth)
  const currentCategory = location.pathname.startsWith("/category/")
    ? location.pathname.split("/")[2] || "latestNews"
    : "latestNews";

  // ✅ fetch categories once
  useEffect(() => {
    let isCancelled = false;

    (async () => {
      try {
        setLoadingCategories(true);
        const data = await fetchCategories();
        if (isCancelled) return;

        const cats = data || [];
        setCategories(cats);

        // If user lands on "/" and latestNews doesn't exist,
        // redirect to first category once
        const onRoot = location.pathname === "/";
        if (onRoot) {
          const hasLatest = cats.some((c) => c.category === "latestNews");
          if (!hasLatest && cats.length > 0) {
            navigate(`/category/${cats[0].category}`, { replace: true });
          }
        }
      } catch (err) {
        console.error("Error loading categories", err);
        if (!isCancelled) setCatError("कॅटेगरी लोड करण्यात अडचण आली.");
      } finally {
        if (!isCancelled) setLoadingCategories(false);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [navigate, location.pathname]);

  const displayTitleMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => (map[c.category] = c.displayTitle));
    return map;
  }, [categories]);

  // ✅ on click: only navigate (menu scroll logic stays smooth)
  const handleCategoryChange = (category) => {
    if (category === currentCategory) return;
    navigate(`/category/${category}`);
  };

  return (
    <div className="app-root">
      <div className="app-shell">
        <Header />

        {loadingCategories && (
          <div className="loading">कॅटेगरी लोड होत आहेत...</div>
        )}
        {catError && <div className="error-message">{catError}</div>}

        {!loadingCategories && !catError && (
          <>
            <CategoryMenu
              categories={categories}
              currentCategory={currentCategory}
              onChangeCategory={handleCategoryChange}
            />

            <main className="main-content">
              <Routes>
                {/* Root redirects to latestNews with shareable URL */}
                <Route
                  path="/"
                  element={<Navigate to="/category/latestNews" replace />}
                />

                {/* ONE NewsList route only => no blink on category changes */}
                <Route
                  path="/category/:categoryId?"
                  element={<NewsListRoute displayTitleMap={displayTitleMap} />}
                />
                <Route
                  path="/hashtag/:tagValue"
                  element={<HashtagNewsList />}
                />

                <Route path="/story/:slug" element={<StoryPage />} />
                <Route path="/HTML/:slug" element={<StoryPage />} />
              </Routes>
            </main>
          </>
        )}

        <FooterBar />
      </div>
    </div>
  );
}

export default AppShell;
